import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentTestPeriod } from '../hooks/useTestPeriod';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, Download, RefreshCw, Users, AlertTriangle,
  TrendingUp, Shield, ShieldAlert, ShieldQuestion,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area,
} from 'recharts';
import { authAPI, researchAPI, exportAPI, adminAPI } from '../services/api';
import type {
  ResearchOverview, LegalStatusDaily, TemporalHeatmapCell,
  PlatformComparison, AdvertiserProfile, ExposureDistribution,
  CategoryCooccurrence, WeeklyTrend, ParticipationData,
} from '../types';
import DateRangePresets from '../components/DateRangePresets';
import KpiCard from '../components/KpiCard';
import ErrorBoundary from '../components/ErrorBoundary';
import CountryFilter, { type Country } from '../components/CountryFilter';
import { GAMBLING_COLORS, LEGAL_COLORS } from '../lib/chartColors';

const TABS = [
  'tabGamblingExposure',
  'tabPlatformComparison',
  'tabTemporalPatterns',
  'tabAdvertisers',
  'tabVolunteerDist',
  'tabParticipation',
  'tabCooccurrence',
] as const;

type TabKey = typeof TABS[number];

const fmt = (v?: number | string | null) => {
  if (v === null || v === undefined) return '-';
  const n = typeof v === 'string' ? Number(v) : v;
  return isNaN(n) ? '-' : n.toLocaleString();
};
const pct = (a: number | string, b: number | string) => {
  const na = Number(a), nb = Number(b);
  return nb > 0 ? `${(100 * na / nb).toFixed(1)}%` : '-';
};

// Map URL ?tab=... shorthand into our TabKey enum.
function tabFromUrl(raw: string | null): TabKey | null {
  if (!raw) return null;
  const map: Record<string, TabKey> = {
    'gambling-exposure': 'tabGamblingExposure',
    'platform-comparison': 'tabPlatformComparison',
    'temporal-patterns': 'tabTemporalPatterns',
    'advertisers': 'tabAdvertisers',
    'volunteer-dist': 'tabVolunteerDist',
    'participation': 'tabParticipation',
    'cooccurrence': 'tabCooccurrence',
    // also accept the camelCase TabKey directly
    tabGamblingExposure: 'tabGamblingExposure',
    tabPlatformComparison: 'tabPlatformComparison',
    tabTemporalPatterns: 'tabTemporalPatterns',
    tabAdvertisers: 'tabAdvertisers',
    tabVolunteerDist: 'tabVolunteerDist',
    tabParticipation: 'tabParticipation',
    tabCooccurrence: 'tabCooccurrence',
  };
  return map[raw] ?? null;
}

export default function ResearchDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>(
    () => tabFromUrl(searchParams.get('tab')) ?? 'tabGamblingExposure',
  );

  // Data states
  const [overview, setOverview] = useState<ResearchOverview | null>(null);
  const [legalStatus, setLegalStatus] = useState<LegalStatusDaily[]>([]);
  const [heatmap, setHeatmap] = useState<TemporalHeatmapCell[]>([]);
  const [platformComparison, setPlatformComparison] = useState<PlatformComparison[]>([]);
  const [advertisers, setAdvertisers] = useState<AdvertiserProfile[]>([]);
  const [distribution, setDistribution] = useState<ExposureDistribution | null>(null);
  const [cooccurrence, setCooccurrence] = useState<CategoryCooccurrence[]>([]);
  const [weeklyTrends, setWeeklyTrends] = useState<WeeklyTrend[]>([]);
  const [participation, setParticipation] = useState<ParticipationData | null>(null);

  // Filters — default: first day of month to today, but URL params from a
  // dashboard drill-down override.
  const [dateFrom, setDateFrom] = useState(() => {
    const fromUrl = searchParams.get('dateFrom');
    if (fromUrl) return fromUrl;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [dateTo, setDateTo] = useState(() => {
    const fromUrl = searchParams.get('dateTo');
    if (fromUrl) return fromUrl;
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [platform, setPlatform] = useState(() => searchParams.get('platform') ?? 'all');
  const [memberCode, setMemberCode] = useState(() => searchParams.get('memberCode') ?? 'all');
  const [country, setCountry] = useState<Country>(() => {
    const v = searchParams.get('country');
    return v === 'JP' || v === 'VN' ? v : 'all';
  });
  const [memberOptions, setMemberOptions] = useState<{ code: string; memberName: string | null }[]>([]);
  const [heatmapGamblingOnly, setHeatmapGamblingOnly] = useState(false);

  // Default the date range to the configured current test/survey period (once),
  // unless a URL drill-down already specified a range.
  const surveyPeriod = useCurrentTestPeriod();
  const periodAppliedRef = useRef(false);
  useEffect(() => {
    if (periodAppliedRef.current || !surveyPeriod) return;
    periodAppliedRef.current = true;
    if (!searchParams.get('dateFrom')) {
      setDateFrom(surveyPeriod.dateFrom);
      setDateTo(surveyPeriod.dateTo);
    }
  }, [surveyPeriod, searchParams]);

  // Export modal
  const [showExport, setShowExport] = useState(false);

  // Date inputs are plain JST calendar dates, but the API compares them as timestamptz —
  // a bare '2026-07-06' is UTC midnight = 09:00 JST, silently dropping 9h at each end
  // (survey window read 1,863 instead of 1,916). Send explicit JST-bounded instants.
  const jstFrom = (d: string) => `${d}T00:00:00+09:00`;
  const jstTo   = (d: string) => `${d}T23:59:59+09:00`;

  const filterOpts = useCallback(() => ({
    dateFrom: dateFrom ? jstFrom(dateFrom) : undefined,
    dateTo: dateTo ? jstTo(dateTo) : undefined,
    platform: platform !== 'all' ? platform : undefined,
    memberCode: memberCode !== 'all' ? memberCode : undefined,
    country: country !== 'all' ? country : undefined,
  }), [dateFrom, dateTo, platform, memberCode, country]);

  // Load member dropdown once on mount (lightweight: codes that have any ad event)
  useEffect(() => {
    adminAPI.getCodesWithAdCounts()
      .then(rows => setMemberOptions(rows))
      .catch(() => setMemberOptions([]));
  }, []);

  function resetFilters() {
    const today = new Date().toISOString().split('T')[0];
    const firstOfMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
    setDateFrom(firstOfMonth);
    setDateTo(today);
    setPlatform('all');
    setMemberCode('all');
    setCountry('all');
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const opts = filterOpts();
      const [ov, ls, pc, adv, dist, co, wt, part] = await Promise.all([
        researchAPI.getOverview({ country: opts.country }),
        researchAPI.getLegalStatus(opts),
        researchAPI.getPlatformComparison(opts),
        researchAPI.getAdvertiserProfiles(opts),
        researchAPI.getExposureDistribution(opts),
        researchAPI.getCategoryCooccurrence(opts),
        researchAPI.getWeeklyTrends(opts),
        researchAPI.getParticipation({ country: opts.country }),
      ]);
      setOverview(ov);
      setLegalStatus(ls);
      setPlatformComparison(pc);
      setAdvertisers(adv);
      setDistribution(dist);
      setCooccurrence(co);
      setWeeklyTrends(wt);
      setParticipation(part);
    } catch (e) {
      console.error('Failed to load research data', e);
    }
    setLoading(false);
  }, [filterOpts]);

  const loadHeatmap = useCallback(async () => {
    try {
      const data = await researchAPI.getTemporalHeatmap({
        ...filterOpts(),
        gamblingOnly: heatmapGamblingOnly,
      });
      setHeatmap(data);
    } catch (e) {
      console.error('Heatmap load error', e);
    }
  }, [filterOpts, heatmapGamblingOnly]);

  // Auth first — do NOT load data from inside this effect (empty deps => it closes over
  // the pre-default, empty date range, and resolves after the survey-period default lands,
  // overwriting the filtered result with an unfiltered one).
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    authAPI.checkAdminAuth().then(r => {
      if (!r.authenticated) navigate('/admin');
      else setAuthed(true);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (authed) loadHeatmap(); }, [heatmapGamblingOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single source of loading: once authed, and on every filter change (incl. the
  // asynchronously-applied default survey period).
  useEffect(() => {
    if (!authed) return;
    loadData();
    loadHeatmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, country, dateFrom, dateTo, platform, memberCode]);

  // Derived data for charts
  const legalPieSummary = (() => {
    const m: Record<string, number> = {};
    legalStatus.forEach(d => { m[d.legalStatus] = (m[d.legalStatus] || 0) + d.adCount; });
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  })();

  const weeklyGamblingData = (() => {
    const byWeek: Record<string, Record<string, number>> = {};
    weeklyTrends.filter(d => d.categoryGroup !== 'non_gambling').forEach(d => {
      if (!byWeek[d.weekStart]) byWeek[d.weekStart] = {};
      byWeek[d.weekStart][d.categoryGroup] = (byWeek[d.weekStart][d.categoryGroup] || 0) + d.adCount;
    });
    return Object.entries(byWeek)
      .map(([week, cats]) => ({ week, ...cats }))
      .sort((a, b) => a.week.localeCompare(b.week));
  })();

  const heatmapGrid = (() => {
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    heatmap.forEach(cell => {
      if (cell.dayOfWeek >= 0 && cell.dayOfWeek < 7 && cell.hourOfDay >= 0 && cell.hourOfDay < 24) {
        grid[cell.dayOfWeek][cell.hourOfDay] += cell.adCount;
      }
    });
    return grid;
  })();

  const heatmapMax = Math.max(1, ...heatmapGrid.flat());
  const dayLabels = [t('research.sun'), t('research.mon'), t('research.tue'), t('research.wed'), t('research.thu'), t('research.fri'), t('research.sat')];

  const histogramBuckets = (() => {
    if (!distribution) return [];
    const buckets: Record<string, number> = {};
    distribution.volunteers.forEach(v => {
      const count = v.gamblingAds;
      let label: string;
      if (count === 0) label = '0';
      else if (count <= 5) label = '1-5';
      else if (count <= 10) label = '6-10';
      else if (count <= 20) label = '11-20';
      else if (count <= 50) label = '21-50';
      else label = '50+';
      buckets[label] = (buckets[label] || 0) + 1;
    });
    return ['0', '1-5', '6-10', '11-20', '21-50', '50+'].map(label => ({
      range: label,
      count: buckets[label] || 0,
    }));
  })();

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header — title row */}
        <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-amber-500" />
              {t('research.title')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('research.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { loadData(); loadHeatmap(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-semibold">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </button>
            <button onClick={() => setShowExport(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-semibold">
              <Download className="w-4 h-4" />
              {t('research.export')}
            </button>
          </div>
        </div>
        {/* Filter row — single horizontal toolbar */}
        <div className="flex items-center gap-2 flex-wrap mb-6 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
          <CountryFilter value={country} onChange={setCountry} />
          <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
          <DateRangePresets
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={(f, to) => { setDateFrom(f); setDateTo(to); }}
            surveyPeriod={surveyPeriod ? { from: surveyPeriod.dateFrom, to: surveyPeriod.dateTo } : null}
          />
          <span className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1" />
          <select value={platform} onChange={e => setPlatform(e.target.value)}
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-xs">
            <option value="all">{t('research.allPlatforms')}</option>
            <option value="youtube">YouTube</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="x_twitter">X (Twitter)</option>
            <option value="tiktok">TikTok</option>
          </select>
          <select value={memberCode} onChange={e => setMemberCode(e.target.value)}
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-xs max-w-[180px]">
            <option value="all">{t('research.allVolunteers', 'All Volunteers')}</option>
            {memberOptions.map(m => (
              <option key={m.code} value={m.code}>
                {m.memberName ? `${m.memberName} (${m.code})` : m.code}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={resetFilters}
            className="px-2.5 py-1 rounded text-xs font-semibold bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200"
            title={t('research.resetFilters', 'Reset filters') as string}
          >
            {t('common.reset')}
          </button>
        </div>

        {/* KPI Cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <KpiCard label={t('research.totalVolunteers')} value={`${fmt(overview.activeVolunteers7d)} / ${fmt(overview.totalVolunteers)}`} sub={t('research.activeVolunteers')} icon={Users} color="border-blue-500" />
            <KpiCard label={t('research.totalAdsCaptured')} value={fmt(overview.totalAds)} icon={TrendingUp} color="border-emerald-500" />
            <KpiCard label={t('research.gamblingAds')} value={`${fmt(overview.gamblingAds)}`} sub={pct(overview.gamblingAds, overview.classifiedAds)} icon={ShieldAlert} color="border-red-500" />
            <KpiCard label={t('research.illegalRate')} value={`${fmt(overview.illegalGamblingAds)}`} sub={pct(overview.illegalGamblingAds, overview.gamblingAds)} icon={AlertTriangle} color="border-orange-500" />
            <KpiCard label={t('research.classificationCoverage')} value={pct(overview.classifiedAds, overview.totalAds)} icon={Shield} color="border-purple-500" />
            <KpiCard label={t('research.enrichmentRate')} value={pct(overview.enrichedAds, overview.totalAds)} icon={ShieldQuestion} color="border-cyan-500" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? 'bg-white dark:bg-gray-800 text-amber-600 dark:text-amber-400 border border-b-0 border-gray-200 dark:border-gray-700'
                  : 'bg-gray-100 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}>
              {t(`research.${tab}`)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" /> {t('common.loading')}
            </div>
          ) : (
            <ErrorBoundary>
              {/* TAB: Gambling Exposure */}
              {activeTab === 'tabGamblingExposure' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.weeklyGamblingByCategory')}</h3>
                  {weeklyGamblingData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={weeklyGamblingData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                        <Legend />
                        {Object.keys(GAMBLING_COLORS).map(cat => (
                          <Bar key={cat} dataKey={cat} stackId="a" fill={GAMBLING_COLORS[cat]} name={t(`categories.${cat}`)} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-10">{t('research.noDataAvailable')}</p>}

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-4">{t('research.legalVsIllegal')}</h3>
                  {(() => {
                    const totalGambling = legalPieSummary.reduce((s, d) => s + d.value, 0);
                    if (totalGambling === 0) {
                      return (
                        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded text-sm text-blue-900 dark:text-blue-200">
                          ℹ {t('sampleWarning.noData')}
                        </div>
                      );
                    }
                    return (
                      <>
                        {totalGambling < 50 && (
                          <div className="mb-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 rounded text-xs text-yellow-900 dark:text-yellow-200">
                            ⚠ {t('sampleWarning.lowSample', { count: totalGambling })}
                          </div>
                        )}
                        <div className="flex flex-col items-center">
                          <ResponsiveContainer width={400} height={300}>
                            <PieChart>
                              <Pie data={legalPieSummary} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name"
                                onClick={(data: any) => {
                                  // Click-through: forward ALL active research filters to CapturedAds
                                  // so the destination page shows the same scope the researcher was
                                  // looking at, plus the slice's category. Other CapturedAds filters
                                  // (event, status, member) are explicitly reset to "all".
                                  const catFilter = data.name === 'illegal' ? 'gambling_illegal'
                                                   : data.name === 'legal' ? 'gambling_legal'
                                                   : data.name === 'gray_zone' ? 'gambling_gray'
                                                   : 'gambling_all';
                                  const qs = new URLSearchParams();
                                  qs.set('category', catFilter);
                                  if (dateFrom)              qs.set('dateFrom', dateFrom);
                                  if (dateTo)                qs.set('dateTo',   dateTo);
                                  if (platform !== 'all')    qs.set('platform', platform);
                                  if (memberCode !== 'all')  qs.set('memberCode', memberCode);
                                  qs.set('country', 'all'); // override CapturedAds default 'JP'
                                  qs.set('eventType', 'all');
                                  qs.set('enrichment', 'all');
                                  navigate(`/admin/captured-ads?${qs.toString()}`);
                                }}
                                style={{ cursor: 'pointer' }}
                                label={({ name, value, percent }) => `${t(`research.${name}` as any) || name} ${value}件 (${(percent * 100).toFixed(0)}%)`}>
                                {legalPieSummary.map(entry => (
                                  <Cell key={entry.name} fill={LEGAL_COLORS[entry.name] || '#6b7280'} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(v: any, n: any) => [`${v}件`, t(`research.${n}` as any) || n]} />
                            </PieChart>
                          </ResponsiveContainer>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            💡 {t('research.clickPieHint', 'Click any slice to view those individual ads')}
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* TAB: Platform Comparison */}
              {activeTab === 'tabPlatformComparison' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.gamblingAdsByPlatform')}</h3>
                  {platformComparison.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={platformComparison.filter(p => p.platform !== 'generic')}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="platform" stroke="#9ca3af" />
                        <YAxis stroke="#9ca3af" />
                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                        <Legend />
                        <Bar dataKey="pachinko" fill={GAMBLING_COLORS.gambling_pachinko} name={t('categories.gambling_pachinko')} />
                        <Bar dataKey="publicGambling" fill={GAMBLING_COLORS.gambling_public} name={t('categories.gambling_public')} />
                        <Bar dataKey="illegalGambling" fill={GAMBLING_COLORS.gambling_online} name={t('categories.gambling_online')} />
                        <Bar dataKey="gachaPremium" fill={GAMBLING_COLORS.gambling_gacha_premium} name={t('categories.gambling_gacha_premium')} />
                        <Bar dataKey="gameGambling" fill={GAMBLING_COLORS.gambling_game} name={t('categories.gambling_game')} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-10">{t('research.noDataAvailable')}</p>}

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.platform')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.totalAds')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.gamblingAds')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.gamblingPct')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.reach')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {platformComparison.filter(p => p.platform !== 'generic').map(p => (
                          <tr key={p.platform} className="border-b dark:border-gray-700/50">
                            <td className="py-2 px-3 font-medium text-gray-900 dark:text-white">{t(`platforms.${p.platform}` as any) || p.platform}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(p.totalAds)}</td>
                            <td className="py-2 px-3 text-right text-red-600 dark:text-red-400 font-semibold">{fmt(p.gamblingAds)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{p.gamblingPct ? `${p.gamblingPct}%` : '-'}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(p.uniqueUsers)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB: Temporal Patterns */}
              {activeTab === 'tabTemporalPatterns' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('research.timeHeatmap')}</h3>
                    <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <input type="checkbox" checked={heatmapGamblingOnly} onChange={e => setHeatmapGamblingOnly(e.target.checked)}
                        className="rounded" />
                      {t('research.gamblingOnly')}
                    </label>
                  </div>

                  <div className="overflow-x-auto">
                    <div className="min-w-[700px]">
                      <div className="grid grid-cols-[60px_repeat(24,1fr)] gap-[2px]">
                        <div />
                        {Array.from({ length: 24 }, (_, i) => (
                          <div key={i} className="text-center text-xs text-gray-400">{i}</div>
                        ))}
                        {heatmapGrid.map((row, dayIdx) => (
                          <div key={dayIdx} className="contents">
                            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">{dayLabels[dayIdx]}</div>
                            {row.map((val, hourIdx) => {
                              const intensity = val / heatmapMax;
                              const bg = heatmapGamblingOnly
                                ? `rgba(239, 68, 68, ${Math.max(0.05, intensity)})`
                                : `rgba(59, 130, 246, ${Math.max(0.05, intensity)})`;
                              return (
                                <div key={hourIdx} className="aspect-square rounded-sm flex items-center justify-center"
                                  style={{ backgroundColor: bg }}
                                  title={`${dayLabels[dayIdx]} ${hourIdx}:00 - ${val} ads`}>
                                  {val > 0 && <span className="text-[9px] text-white/80">{val > 999 ? `${Math.round(val/1000)}k` : val}</span>}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-4">{t('research.weeklyTrends')}</h3>
                  {weeklyTrends.length > 0 ? (() => {
                    const byWeek: Record<string, Record<string, number>> = {};
                    weeklyTrends.forEach(d => {
                      if (!byWeek[d.weekStart]) byWeek[d.weekStart] = {};
                      const key = d.categoryGroup === 'non_gambling' ? 'non_gambling' : 'gambling';
                      byWeek[d.weekStart][key] = (byWeek[d.weekStart][key] || 0) + d.adCount;
                    });
                    const data = Object.entries(byWeek).map(([week, vals]) => ({ week, ...vals })).sort((a, b) => a.week.localeCompare(b.week));
                    return (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                          <YAxis stroke="#9ca3af" />
                          <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                          <Legend />
                          <Area type="monotone" dataKey="gambling" fill="#ef4444" stroke="#ef4444" fillOpacity={0.3} name="Gambling" />
                          <Area type="monotone" dataKey="non_gambling" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.1} name="Non-gambling" />
                        </AreaChart>
                      </ResponsiveContainer>
                    );
                  })() : <p className="text-gray-400 text-center py-10">{t('research.noDataAvailable')}</p>}
                </div>
              )}

              {/* TAB: Advertisers */}
              {activeTab === 'tabAdvertisers' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.advertiserRanking')}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.advertiser')}</th>
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.domain')}</th>
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.category')}</th>
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.platform')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.impressions')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.reach')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.daysActive')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.avgConfidence')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {advertisers.map((a, i) => (
                          <tr key={i} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="py-2 px-3 font-medium text-gray-900 dark:text-white">{a.advertiser}</td>
                            <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs font-mono">{a.domain || '-'}</td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: (GAMBLING_COLORS[a.category] || '#6b7280') + '20', color: GAMBLING_COLORS[a.category] || '#6b7280' }}>
                                {t(`categories.${a.category}` as any) || a.category}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-700 dark:text-gray-300">{t(`platforms.${a.platform}` as any) || a.platform}</td>
                            <td className="py-2 px-3 text-right font-semibold text-gray-900 dark:text-white">{fmt(a.impressions)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(a.reach)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{a.daysActive}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{a.avgConfidence || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {advertisers.length === 0 && <p className="text-gray-400 text-center py-10">{t('research.noDataAvailable')}</p>}
                  </div>
                </div>
              )}

              {/* TAB: Volunteer Distribution */}
              {activeTab === 'tabVolunteerDist' && distribution && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.exposureHistogram')}</h3>
                  {histogramBuckets.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={histogramBuckets}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="range" stroke="#9ca3af" label={{ value: t('research.gamblingAds'), position: 'bottom', fontSize: 12 }} />
                        <YAxis stroke="#9ca3af" label={{ value: 'Volunteers', angle: -90, position: 'insideLeft', fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                        <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : null}

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-4">{t('research.summaryStats')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                    {(['count', 'mean', 'median', 'p25', 'p75', 'stdDev', 'max'] as const).map(key => (
                      <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t(`research.${key === 'count' ? 'totalVolunteers' : key === 'stdDev' ? 'stdDev' : key}` as any) || key}</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-white">{distribution.summary[key]}</p>
                      </div>
                    ))}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mt-8 mb-4">{t('research.tabVolunteerDist')}</h3>
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white dark:bg-gray-800">
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.volunteerId')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.totalAds')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.gamblingAds')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.gamblingPct')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.activeDays')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.adsPerDay')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {distribution.volunteers.map(v => (
                          <tr key={v.volunteerId} className="border-b dark:border-gray-700/50">
                            <td className="py-1.5 px-3 font-mono text-xs text-gray-900 dark:text-white">{v.volunteerId}</td>
                            <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(v.totalAds)}</td>
                            <td className="py-1.5 px-3 text-right font-semibold text-red-600 dark:text-red-400">{fmt(v.gamblingAds)}</td>
                            <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{v.gamblingPct ?? '-'}%</td>
                            <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{v.activeDays}</td>
                            <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{v.adsPerDay ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB: Participation */}
              {activeTab === 'tabParticipation' && participation && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.participationSummary')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{participation.summary.total}</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">{t('research.totalVolunteers')}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-600 dark:text-green-400">{participation.summary.active}</p>
                      <p className="text-sm text-green-700 dark:text-green-300">{t('research.activeParticipants')}</p>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{participation.summary.stale}</p>
                      <p className="text-sm text-orange-700 dark:text-orange-300">{t('research.staleParticipants')}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">{participation.summary.noData}</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{t('research.noDataParticipants')}</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white dark:bg-gray-800">
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.volunteerId')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.activeDays')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.totalAds')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.gamblingAds')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.platformsUsed')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.daysInactive')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participation.volunteers.map(v => {
                          const isStale = v.daysSinceLastEvent !== null && v.daysSinceLastEvent > 7;
                          return (
                            <tr key={v.volunteerId} className={`border-b dark:border-gray-700/50 ${isStale ? 'bg-orange-50/50 dark:bg-orange-900/10' : ''}`}>
                              <td className="py-1.5 px-3">
                                <span className="font-mono text-xs text-gray-900 dark:text-white">{v.volunteerId}</span>
                                {v.memberName && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{v.memberName}</span>}
                              </td>
                              <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{v.activeDays}</td>
                              <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(v.totalEvents)}</td>
                              <td className="py-1.5 px-3 text-right font-semibold text-red-600 dark:text-red-400">{fmt(v.gamblingEvents)}</td>
                              <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">{v.platformsUsed}</td>
                              <td className="py-1.5 px-3 text-right">
                                {v.daysSinceLastEvent !== null ? (
                                  <span className={isStale ? 'text-orange-600 dark:text-orange-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                                    {Math.round(v.daysSinceLastEvent)}d
                                    {isStale && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                                  </span>
                                ) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TAB: Co-occurrence */}
              {activeTab === 'tabCooccurrence' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('research.cooccurrenceTitle')}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{t('research.cooccurrenceDesc')}</p>
                  {cooccurrence.length > 0 ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={cooccurrence.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis type="number" stroke="#9ca3af" />
                        <YAxis dataKey="category" type="category" width={120} stroke="#9ca3af" tick={{ fontSize: 11 }}
                          tickFormatter={(val: string) => t(`categories.${val}` as any) || val} />
                        <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: 8, color: '#fff' }} />
                        <Bar dataKey="adCount" fill="#3b82f6" name={t('research.adCount')} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-10">{t('research.noDataAvailable')}</p>}

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.category')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.adCount')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.userCount')}</th>
                          <th className="text-right py-2 px-3 text-gray-600 dark:text-gray-400">{t('research.pctGamblingUsers')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cooccurrence.map(c => (
                          <tr key={c.category} className="border-b dark:border-gray-700/50">
                            <td className="py-2 px-3 text-gray-900 dark:text-white">{t(`categories.${c.category}` as any) || c.category}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(c.adCount)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{fmt(c.userCount)}</td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{c.pctOfGamblingUsers ?? '-'}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </ErrorBoundary>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {showExport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowExport(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('research.export')}</h3>
            <div className="space-y-3">
              <a href={exportAPI.aggregatedUrl({ dateFrom, dateTo })} target="_blank" rel="noopener noreferrer"
                className="block w-full px-4 py-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg text-blue-700 dark:text-blue-300 font-medium transition-colors">
                <Download className="inline w-4 h-4 mr-2" />{t('research.exportAggregated')}
              </a>
              <a href={exportAPI.aggregatedUrl({ dateFrom, dateTo, anonymize: true })} target="_blank" rel="noopener noreferrer"
                className="block w-full px-4 py-3 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg text-purple-700 dark:text-purple-300 font-medium transition-colors">
                <Download className="inline w-4 h-4 mr-2" />{t('research.exportAnonymized')}
              </a>
              <a href={exportAPI.gamblingDetailUrl({ dateFrom, dateTo })} target="_blank" rel="noopener noreferrer"
                className="block w-full px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-red-700 dark:text-red-300 font-medium transition-colors">
                <Download className="inline w-4 h-4 mr-2" />{t('research.exportGamblingDetail')}
              </a>
            </div>
            <button onClick={() => setShowExport(false)}
              className="mt-4 w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
